import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AccountCircle as AccountCircleIcon,
    Notifications as ChatBubbleOutlineIcon,
    Close as CloseIcon,
    DarkMode as DarkModeIcon,
    Dashboard as DashboardIcon,
    LightMode as LightModeIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon,
    Person as PersonIcon,
    SettingsRounded as SettingsRoundedIcon,
    TableChart as TableChartIcon,
    Category as CategoryIcon,
    Language,
} from '@mui/icons-material';
import NoteAlt from '@mui/icons-material/NoteAlt';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TableBarIcon from '@mui/icons-material/TableBar';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import MaleIcon from '@mui/icons-material/Person';
import FemaleIcon from '@mui/icons-material/Person2';
import BarChartIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/Receipt';
import Groups from '@mui/icons-material/Groups';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ScienceIcon from '@mui/icons-material/Science';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ColorContext } from '../../ColorContext/darkContext';
import { cachedAPI, logoutUser, navbarAPI } from '../../services/apiService';
import './navbar.css';

function Navbar() {
    const { darkMode, dispatch } = useContext(ColorContext);
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupOpen, setGroupOpen] = useState(false);
    const groupRef = useRef(null);
    const [userInfo, setUserInfo] = useState({
        first_name: '',
        last_name: '',
        gender: 'M',
        image: '',
    });
    const [videoOpen, setVideoOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const { t } = useTranslation();

    const notificationRef = useRef(null);
    const langRef = useRef(null);
    const videoRef = useRef(null);

    const unreadCount = notifications.length;

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const data = await navbarAPI.getUserInfo();
                setUserInfo(data);
                if (data?.is_staff !== undefined) {
                    localStorage.setItem('is_staff', JSON.stringify(data.is_staff));
                }
                if (data) {
                    const firstName = data.first_name || '';
                    const lastName = data.last_name || '';
                    localStorage.setItem('fullName', `${firstName} ${lastName}`.trim());
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchUser();
    }, []);
    const isStaff = JSON.parse(localStorage.getItem('is_staff'));
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const data = await navbarAPI.getNotifications();
                setNotifications(data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchNotifications();
    }, []);

    const handleMarkAsRead = async (notifUid) => {
        try {
            await navbarAPI.markNotificationAsRead(notifUid);
            setNotifications((prev) => prev.filter((n) => n.uid !== notifUid));
        } catch (err) {
            console.error('❌ Error marking notification as read:', err);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (notificationRef.current && !notificationRef.current.contains(e.target)) {
                setNotificationsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const data = await navbarAPI.getGroups();
                setGroups(data || []);
            } catch (err) {
                console.error('❌ خطا در دریافت گروه‌ها:', err);
            }
        };
        fetchGroups();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (groupRef.current && !groupRef.current.contains(e.target)) {
                setGroupOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) {
                setLangOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (videoRef.current && !videoRef.current.contains(e.target)) {
                setVideoOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        await logoutUser(false);
        navigate('/login');
    };

    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const notifTime = new Date(timestamp);
        const diffMinutes = Math.floor((now - notifTime) / 1000 / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        if (diffMinutes < 60) return t('minutes_ago', { count: diffMinutes });
        if (diffHours < 24)
            return t('hours_minutes_ago', {
                hours: diffHours,
                minutes: diffMinutes % 60,
            });
        return t('days_ago', { count: diffDays });
    };

    const renderUserIcon = () => {
        const iconColor = darkMode ? '#fff' : 'inherit';
        if (userInfo.image) {
            return <img src={userInfo.image} alt="admin" className="admin_pic" />;
        }
        if (userInfo.gender === 'F') {
            return <FemaleIcon fontSize="large" style={{ color: iconColor }} />;
        }
        return <MaleIcon fontSize="large" style={{ color: iconColor }} />;
    };
    const showDisabledToast = () => {
        setToastVisible(true);

        setTimeout(() => {
            setToastVisible(false);
        }, 3000);
    };
    const handleProtectedNavigation = (e, route) => {
        e.preventDefault();
        if (isStaff) {
            setSidebarOpen(false);
            navigate(route);
        } else {
            showDisabledToast();
        }
    };

    return (
        <nav className={`navbar ${darkMode ? 'dark' : ''}`}>
            <div className="navbar_main">
                <div className="menu_logo">
                    <MenuIcon className="menu_icon" onClick={() => setSidebarOpen(true)} />
                    <Link to="/" className="logo_link" />
                </div>
                <div className="item_lists">
                    <div className="item lang_item" ref={langRef}>
                        <button
                            type="button"
                            className={`lang_button ${darkMode ? 'white' : ''}`}
                            onClick={() => setLangOpen(!langOpen)}
                        >
                            <Language />
                            <span className="lang_text">{t('change_language')}</span>
                        </button>
                        {langOpen && (
                            <div className={`lang_dropdown ${darkMode ? 'dark' : ''}`}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        i18n.changeLanguage('fa');
                                        setLangOpen(false);
                                    }}
                                >
                                    {t('language_fa')}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        i18n.changeLanguage('en');
                                        setLangOpen(false);
                                    }}
                                >
                                    {t('language_en')}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="item">
                        {!darkMode ? (
                            <DarkModeIcon
                                className="item_icon"
                                onClick={() => dispatch({ type: 'TOGGLE' })}
                            />
                        ) : (
                            <LightModeIcon
                                className="item_icon white"
                                onClick={() => dispatch({ type: 'TOGGLE' })}
                            />
                        )}
                    </div>

                    <div className="item">
                        <OndemandVideoIcon
                            className={`item_icon ${darkMode ? 'white' : ''}`}
                            onClick={() => setVideoOpen(true)}
                        />
                    </div>

                    <div className="item" ref={notificationRef}>
                        <button
                            type="button"
                            className="notification_button"
                            onClick={() => setNotificationsOpen(!notificationsOpen)}
                        >
                            <ChatBubbleOutlineIcon
                                className={`item_icon ${darkMode ? 'white' : ''}`}
                            />
                            {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
                        </button>
                        {notificationsOpen && (
                            <div className={`notification-dropdown ${darkMode ? 'dark' : ''}`}>
                                <div className="dropdown-header">
                                    <h4>{t('notifications')}</h4>
                                    <span>
                                        {unreadCount > 0
                                            ? t('new_notifications', { count: unreadCount })
                                            : t('no_new_notifications')}
                                    </span>
                                </div>
                                <div className="dropdown-body">
                                    {notifications.length > 0 ? (
                                        notifications.map((n) => (
                                            <div key={n.id} className="notification-row">
                                                <div className="notification-text">
                                                    <p className="message">{n.message}</p>
                                                    <span className="time">
                                                        {getRelativeTime(n.created_at)}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="mark-read-btn"
                                                    onClick={() => handleMarkAsRead(n.uid)}
                                                >
                                                    ✓
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-state">
                                            <p>{t('no_notifications')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        ref={groupRef}
                        type="button"
                        className="item user_info"
                        onClick={() => setGroupOpen(!groupOpen)}
                    >
                        <div className="admin_pic_div">{renderUserIcon()}</div>
                        <span className="user_name">
                            {userInfo.first_name} {userInfo.last_name}
                        </span>
                        {groupOpen && (
                            <div className={`group_dropdown ${darkMode ? 'dark' : ''}`}>
                                {groups.length > 0 ? (
                                    groups.map((g) => (
                                        <button
                                            key={g.name}
                                            type="button"
                                            className="navbarGroupButton"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                localStorage.setItem('userGroup', g.name);
                                                cachedAPI.invalidateGroupData();
                                                setGroupOpen(false);
                                                window.location.reload();
                                            }}
                                        >
                                            {g.nickname}
                                        </button>
                                    ))
                                ) : (
                                    <p className="empty">{t('no_groups_found')}</p>
                                )}
                            </div>
                        )}
                    </button>
                </div>
            </div>

            <div
                className={`mobile_sidebar ${sidebarOpen ? 'open' : ''} ${darkMode ? 'dark' : ''}`}
            >
                <div className="sidebar_header">
                    <CloseIcon className="close_icon" onClick={() => setSidebarOpen(false)} />
                </div>
                <ul className="mobile_navBar">
                    <p className="spann">{t('main')}</p>
                    <Link to="/" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <DashboardIcon className="icon" />
                            <p className="mobileNavbarText">{t('dashboard')}</p>
                        </li>
                    </Link>
                    <p className="spann">{t('list')}</p>
                    <Link to="/UsersList" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/UsersList')}
                            >
                                <PersonIcon className="icon" />
                                <p className="mobileNavbarText">{t('users')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/products" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <TableChartIcon className="icon" />
                            <p className="mobileNavbarText">{t('products')}</p>
                        </li>
                    </Link>
                    <Link to="/categories" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <CategoryIcon className="icon" />
                            <p className="mobileNavbarText">{t('categories')}</p>
                        </li>
                    </Link>
                    <Link to="/invntory" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/invntory')}
                            >
                                <Inventory2Icon className="icon" />
                                <p className="mobileNavbarText">{t('invntory')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/Formulation" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/Formulation')}
                            >
                                <ScienceIcon className="icon" />
                                <p className="mobileNavbarText">{t('formulations')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/Orders" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/Orders')}
                            >
                                <NoteAlt className="icon" />
                                <p className="mobileNavbarText">{t('orders')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/tables" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/tables')}
                            >
                                <TableBarIcon className="icon" />
                                <p className="mobileNavbarText">{t('tables')}</p>
                            </button>
                        </li>
                    </Link>
                    <p className="spann">{t('Reports')}</p>
                    <Link to="/OrdersReport" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/OrdersReport')}
                            >
                                <AssessmentIcon className="icon" />
                                <p className="mobileNavbarText">{t('OrdersReport')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/SalesComparison" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/SalesComparison')}
                            >
                                <QueryStatsIcon className="icon" />
                                <p className="mobileNavbarText">{t('SalesComparison')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/Finance" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/Finance')}
                            >
                                <CreditCardIcon className="icon" />
                                <p className="mobileNavbarText">{t('transactions')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/Billings" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <button type="button" className="mobile-nav-btn">
                                <BarChartIcon className="icon" />
                                <p className="mobileNavbarText">{t('billings')}</p>
                            </button>
                        </li>
                    </Link>
                    <Link to="/Customers" style={{ textDecoration: 'none' }}>
                        <li>
                            <button
                                type="button"
                                className="mobile-nav-btn"
                                onClick={(e) => handleProtectedNavigation(e, '/Customers')}
                            >
                                <Groups className="icon" />
                                <p className="mobileNavbarText"> {t('customers_service')}</p>
                            </button>
                        </li>
                    </Link>
                    <p className="spann">{t('settings')}</p>
                    <Link to="/profile" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <AccountCircleIcon className="icon" />
                            <p className="mobileNavbarText">{t('profilee')}</p>
                        </li>
                    </Link>
                    <Link to="/settings" onClick={() => setSidebarOpen(false)}>
                        <li>
                            <SettingsRoundedIcon className="icon" />
                            <p className="mobileNavbarText">{t('settings')}</p>
                        </li>
                    </Link>
                    <li>
                        <button onClick={handleLogout} type="button" className="logout-btn">
                            <LogoutIcon className="icon" />
                            <p className="mobileNavbarText">{t('logout')}</p>
                        </button>
                    </li>
                </ul>
            </div>
            {toastVisible && <div className="custom-toast">{t('feature_disabled')}</div>}

            {videoOpen && (
                <div className="video-modal">
                    <div className="video-modal-content" ref={videoRef}>
                        <button
                            type="button"
                            className="close-video"
                            onClick={() => setVideoOpen(false)}
                        >
                            ✕
                        </button>
                        <video controls autoPlay>
                            <source
                                src="https://meline-items.storage.iran.liara.space/Video/complete.mp4"
                                type="video/mp4"
                            />
                            <track kind="captions" />
                        </video>
                    </div>
                </div>
            )}
        </nav>
    );
}

export default Navbar;
